import { Observable } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApplicationId } from '../../../lib';
import { RBR_API_CONFIG, RbrApiConfig } from '../rbr-api.config';
import { ConfigurationFields, Endpoints, ApiLevel, ApiRoom, ReferenceEnergyDemand } from '../models';
import { PRESERVE_NULL_KEYS } from '../tokens';
import { ApiBuilding } from '../models/api/api-building.interface';
import { ApiProject } from '../models/api/api-project.interface';

/**
 * The structure of the request body when patching a project in the VGP API.
 *
 * 🚨 INFO: Building sub-properties can be patched independently, but each patched sub-property
 * must be sent as a complete object (e.g. full levels dictionary or full rooms dictionary).
 * Adding or modifying a single level/room still requires sending the full dictionary for that sub-property.
 */
export interface PatchApiProjectRequest {
    building: ApiBuilding;
}

@Injectable({ providedIn: 'root' })
export class RoomByRoomApiService {
    private readonly baseUrl: string;
    private readonly applicationId: ApplicationId = ApplicationId.ViGuidePlanning;

    constructor(
        @Inject(RBR_API_CONFIG) config: RbrApiConfig,
        private httpClient: HttpClient
    ) {
        this.baseUrl = config.apiBaseUrl;
        this.applicationId = config.applicationId;
    }

    public getConfiguration$(params: { fields: ConfigurationFields[] }): Observable<any> {
        const url = `${this.baseUrl}${Endpoints.Configurations}`;
        return this.httpClient.get(url, { params });
    }

    public getProject$(projectId: string): Observable<ApiProject> {
        const url = `${this.baseUrl}${Endpoints.Projects}/${projectId}`;
        return this.httpClient.get<ApiProject>(url);
    }

    /**
     * Building fields whose explicit `null` must survive an app-level null-stripping interceptor,
     * so that clearing the custom load factors actually reaches the backend.
     * Hosts without such an interceptor ignore the context and send the `null` as-is.
     */
    private static readonly PRESERVE_NULL_BUILDING_KEYS = ['standardRoomHeatLoad', 'standardRoomCoolingLoad'];

    public patchProject$(projectId: string, body: PatchApiProjectRequest): Observable<unknown> {
        const url = `${this.baseUrl}${Endpoints.Projects}/${projectId}`;
        // Declare which building nulls are intentional; a host null-stripping interceptor reads this to keep them.
        const context = new HttpContext().set(PRESERVE_NULL_KEYS, RoomByRoomApiService.PRESERVE_NULL_BUILDING_KEYS);
        return this.httpClient.patch(url, body, { context });
    }

    public getReferenceEnergyDemand$(projectId: string): Observable<ReferenceEnergyDemand> {
        const url = `${this.baseUrl}${Endpoints.Projects}/${projectId}${Endpoints.ReferenceEnergyDemand}`;
        return this.httpClient.get<ReferenceEnergyDemand>(url);
    }
}
